/**
 * The Settings page's sections, one per vertical tab. Kept separate from the
 * page so the "which sections can this user see, and which one does this URL
 * mean" rules stay testable without rendering the whole page.
 */

export type SettingsSectionId =
  | "branding"
  | "organization"
  | "underwriting-rules"
  | "emails"
  | "ai-assistant"
  | "ai-access";

export interface SettingsSection {
  id: SettingsSectionId;
  label: string;
}

const ORGANIZATION: SettingsSection = {
  id: "organization",
  label: "Organization",
};

const BRANDING: SettingsSection = {
  id: "branding",
  label: "Branding",
};

const UNDERWRITING_RULES: SettingsSection = {
  id: "underwriting-rules",
  label: "Underwriting rules",
};

const EMAILS: SettingsSection = {
  id: "emails",
  label: "Automated emails",
};

const AI_ASSISTANT: SettingsSection = {
  id: "ai-assistant",
  label: "AI Assistant",
};

const AI_ACCESS: SettingsSection = {
  id: "ai-access",
  label: "AI access",
};

interface SectionAccess {
  isOrgManager: boolean;
  /** The organization has the assistant (capabilities.agent). */
  hasAgent?: boolean;
  /** The user chooses who has AI access (capabilities.manage_agent_access). */
  managesAgentAccess?: boolean;
}

/** Sections this user may open, in tab order. Empty means nothing to manage. */
export function visibleSettingsSections({
  isOrgManager,
  hasAgent = false,
  managesAgentAccess = false,
}: SectionAccess): SettingsSection[] {
  if (!isOrgManager) return [];
  return [
    ORGANIZATION,
    BRANDING,
    UNDERWRITING_RULES,
    EMAILS,
    ...(hasAgent ? [AI_ASSISTANT] : []),
    ...(managesAgentAccess ? [AI_ACCESS] : []),
  ];
}

/** Whether the Settings page has anything for this user; the nav and the route both ask this. */
export function canAccessSettings(access: SectionAccess): boolean {
  return visibleSettingsSections(access).length > 0;
}

/**
 * The section a URL means: the requested one when it exists and is allowed,
 * otherwise the first section the user can see (so a stale or hand-typed link
 * lands somewhere useful instead of on a blank page).
 *
 * Legacy `/setting/contracts` bookmarks open Automated emails (contracts UI
 * moved there).
 */
export function resolveSettingsSection(
  requested: string | undefined,
  visible: SettingsSection[]
): SettingsSection | null {
  const normalized = requested === "contracts" ? "emails" : requested;
  const match = visible.find((section) => section.id === normalized);
  return match ?? visible[0] ?? null;
}

/** The OAuth callback returns to `/setting` with a provider result query param. */
export function sectionForOAuthReturn(search: string): SettingsSectionId | null {
  const params = new URLSearchParams(search);
  if (params.has("submissions_inbox_oauth")) return "organization";
  return params.has("contract_email_oauth") ? "emails" : null;
}

const OAUTH_ERROR_MESSAGES: Record<string, string> = {
  denied: "Connection cancelled — you didn't approve access.",
  invalid_state: "That connection link expired or was invalid. Please try connecting again.",
  missing_code: "Connection was interrupted before it finished. Please try again.",
  exchange_failed: "Couldn't complete the connection. Please try again.",
  unknown_provider: "Unsupported provider.",
  mailbox_in_use: "That mailbox is already connected to another organization.",
};

export type ContractOAuthFlash = { type: "success" | "error"; message: string };

/** Read the OAuth result from the query string and the search string without it. */
export function takeContractOAuthFlash(search: string): {
  flash: ContractOAuthFlash | null;
  nextSearch: string;
} {
  const params = new URLSearchParams(search.startsWith("?") ? search.slice(1) : search);
  const submissionsStatus = params.get("submissions_inbox_oauth");
  const contractStatus = params.get("contract_email_oauth");
  const status = submissionsStatus || contractStatus;
  const kind = submissionsStatus ? "submissions" : "contract";
  if (!status) {
    return {
      flash: null,
      nextSearch: search.startsWith("?") ? search : search ? `?${search}` : "",
    };
  }

  let flash: ContractOAuthFlash | null = null;
  if (status === "success") {
    flash = {
      type: "success",
      message:
        kind === "submissions"
          ? "Submissions inbox connected. New mail in that inbox is read here, and forwarding to your plus-address is off."
          : "Mailbox connected. Automated emails will now send from your connected address.",
    };
  } else if (status === "error") {
    const reason = params.get("reason");
    flash = {
      type: "error",
      message:
        (reason && OAUTH_ERROR_MESSAGES[reason]) ||
        "Couldn't connect your mailbox. Please try again.",
    };
  }

  params.delete("contract_email_oauth");
  params.delete("submissions_inbox_oauth");
  params.delete("reason");
  const remaining = params.toString();
  return { flash, nextSearch: remaining ? `?${remaining}` : "" };
}
